import { useMutation, useQuery } from '@apollo/client';
import { InfoOutlined } from '@mui/icons-material';
import { FormSchemaProvider } from '@procureai/ui-form-mui';
import {
  useLanguageCode,
  Widget,
  WidgetContent,
  WidgetHeader,
  WidgetIcon,
  WidgetEditActions,
  useUuid,
  useCustomSnackbar,
} from '@procureai/ui-mui';
import { useCallback, useState } from 'react';
import { SubmitHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useNegotiationDetail } from '../Module.hooks';
import { TsrctLanguage } from '@/generated/gql/graphql';
import { I18nNamespace } from '~/common/types';
import { isNegotiationCaseEditable } from '~/util/negotiationCase/isNegotiationCaseEditable';

import { SupplierIntroductionContent } from './components/SupplierIntroductionContent/SupplierIntroductionContent';
import { SupplierIntroductionForm } from './components/SupplierIntroductionForm/SupplierIntroductionForm';
import {
  SupplierIntroductionFormData,
  supplierIntroductionFormDataSchema,
} from './components/SupplierIntroductionForm/SupplierIntroductionForm.schema';
import { NEGOTIATION_CASE_SUPPLIER_INTRODUCTION_MESSAGE_MUTATION } from './NegotiationSupplierIntroductionMessageMutation.gql';
import { NEGOTIATION_CASE_SUPPLIER_INTRODUCTION_MESSAGE_QUERY } from './NegotiationSupplierIntroductionMessageQuery.gql';

export interface NegotiationContactWidgetProps {
  // widget is part of a bigger form -> do not add own form and saving capabilities
  hasParentForm?: boolean;
}

export const NegotiationSupplierIntroductionWidget = ({ hasParentForm }: NegotiationContactWidgetProps) => {
  const { t } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'NegotiationSupplierIntroductionWidget' });
  const { negotiationId } = useNegotiationDetail();
  const { languageCode } = useLanguageCode();
  const formId = useUuid();
  const [editOpen, setEditOpen] = useState(hasParentForm);
  const { onError, enqueueSuccessSnackbar } = useCustomSnackbar();

  const variables = {
    id: negotiationId,
  };

  const { data, loading } = useQuery(NEGOTIATION_CASE_SUPPLIER_INTRODUCTION_MESSAGE_QUERY, {
    variables,
    skip: !negotiationId,
  });

  const [update, { loading: saving }] = useMutation(NEGOTIATION_CASE_SUPPLIER_INTRODUCTION_MESSAGE_MUTATION, {
    refetchQueries: [{ query: NEGOTIATION_CASE_SUPPLIER_INTRODUCTION_MESSAGE_QUERY, variables }],
    onError,
  });

  const supplierIntroductionMessage = data?.tesseract.negotiationCase.supplierIntroductionMessage ?? {
    text: '',
    language: languageCode,
  };

  const editDisabled = !isNegotiationCaseEditable(data?.tesseract.negotiationCase);

  let content;

  const onSubmit: SubmitHandler<SupplierIntroductionFormData> = (submitData) => {
    if (submitData) {
      (async () => {
        const { errors } = await update({
          variables: {
            id: negotiationId,
            data: {
              text: submitData.text,
              language: submitData.language.toUpperCase() as TsrctLanguage,
            },
          },
        });
        if (!errors) {
          setEditOpen(false);
          enqueueSuccessSnackbar(t('saveSuccess'));
        } else {
          onError(errors);
        }
      })();
    }
  };

  if (editOpen && !saving) {
    content = <SupplierIntroductionForm />;

    if (!hasParentForm) {
      content = (
        <FormSchemaProvider<SupplierIntroductionFormData>
          id={formId}
          data={supplierIntroductionMessage}
          defaultValues={supplierIntroductionMessage}
          schema={supplierIntroductionFormDataSchema}
          onSubmit={onSubmit}
        >
          {content}
        </FormSchemaProvider>
      );
    }
  } else {
    content = <SupplierIntroductionContent data={supplierIntroductionMessage} loading={loading || saving} />;
  }

  const handleEditToggle = useCallback(() => {
    setEditOpen((prev) => !prev);
  }, []);

  return (
    <>
      <Widget hover={false} data-intercom-target="NegotiationSupplierIntroductionWidget">
        <WidgetHeader
          title={t('title')}
          icon={<WidgetIcon icon={<InfoOutlined fontSize="small" />} />}
          actionsRight={
            hasParentForm ? null : (
              <WidgetEditActions
                formId={formId}
                open={editOpen}
                disabled={editDisabled}
                saving={saving}
                onToggle={handleEditToggle}
              />
            )
          }
        />
        <WidgetContent>{content}</WidgetContent>
      </Widget>
    </>
  );
};
